import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import dayjs from 'dayjs/esm/index';
import { firstValueFrom } from 'rxjs';
import { languageHelper } from './language-helper.util';

/**
 * Native port of the v1 `Incentives` factory (account-v1/src/core/api/incentives.js).
 * WS5 #10 Stage A inc 2s: replaces the `$injector.get('Incentives')` grabs
 * (ft-campaign-goals-only / ft-campaign-prestay; contest-details/-invitations later).
 *
 * QUIRKS PRESERVED: `getAvailableIncentives` resolves with an $http-RESPONSE-shaped
 * `{data}` envelope (consumers read `response.data`), the incentive ids are
 * UPPERCASED incentiveUuid, languages get the `code` alias, and the
 * post-remap `angular.isArray(response.data)` sort branch is kept VERBATIM even
 * though it can never fire (response.data is reassigned to the `{item}` OBJECT
 * two lines above — a v1 bug preserved on purpose). Date transform = the v1
 * 'YYYY-M-D' (no zero-pad) composition.
 */
@Injectable({ providedIn: 'root' })
export class IncentivesV1ApiService {
  private readonly http = inject(HttpClient);

  /** v1 `Incentives.toggleStatus(id, status)` — GET (sic). */
  toggleStatus(id: any, status: any): Promise<any> {
    return firstValueFrom(this.http.get('/api/properties/layers/campaigns/goals/' + id + '/activate/' + status));
  }

  /** v1 `Incentives.saveIncentiveCriteria(incentive)`. */
  saveIncentiveCriteria(incentive: any): Promise<any> {
    return firstValueFrom(
      this.http.put('/api/properties/layers/campaigns/goals/' + incentive.id, this.transformToApiFormat(incentive)),
    );
  }

  /** v1 `Incentives.createIncentiveCriteria(incentive)`. */
  createIncentiveCriteria(incentive: any): Promise<any> {
    return firstValueFrom(
      this.http.post('/api/touchpoints/incentives/criteria/' + incentive.id, this.transformToApiFormat(incentive)),
    );
  }

  /** v1 `Incentives.getAvailableIncentives(propertyId)` — the `{data}` envelope. */
  getAvailableIncentives(propertyId: string): Promise<{ data: any }> {
    const url = '/api/' + propertyId + '/properties/' + propertyId + '/incentives';
    return firstValueFrom(this.http.get<any[]>(url)).then((body: any) => {
      const mappedResponse: any = { item: body };
      mappedResponse.item = [
        ...mappedResponse.item.map((item: any) => ({
          ...item,
          id: item.incentiveUuid.toUpperCase(),
          languages: {
            item: item.languages.map((language: any) => ({ ...language, code: language.languageCode })),
          },
        })),
      ];
      const response = { data: mappedResponse as any };
      if (Array.isArray(response.data)) {
        // v1 dead branch (response.data is the {item} object here) — kept verbatim.
        response.data.forEach((incentive: any) => languageHelper.sortByDefaultLanguage(incentive.languages));
      }
      return response;
    });
  }

  private transformToApiFormat(incentive: any): any {
    if (incentive.type == '1') return this.transformIncentiveToAdvocateIncentive(incentive);
    return this.transformIncentiveToFriendIncentive(incentive);
  }

  private transformIncentiveToFriendIncentive(incentive: any): any {
    return {
      daysValidFor: incentive.daysValidFor,
      expirationDate: this.transformDate(incentive.expirationDate),
    };
  }

  private transformIncentiveToAdvocateIncentive(incentive: any): any {
    return {
      clicks: incentive.clicks,
      signups: incentive.signups,
      nightRangeStart: incentive.nightRangeStart,
      nightRangeEnd: incentive.nightRangeEnd,
      expirationDate: this.transformDate(incentive.expirationDate),
    };
  }

  private transformDate(dateString: any): string {
    let rc = '';
    if (dateString) {
      const year = dayjs(dateString).format('YYYY');
      const month = dayjs(dateString).format('M');
      const day = dayjs(dateString).format('D');
      rc = year + '-' + month + '-' + day;
    }
    return rc;
  }
}
